import dayjs from 'dayjs'
import { MoreHorizontal } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { SettingsSection } from '~/components/common/SettingsSection'
import { useUserContext } from '~/contexts/UserContext'
import { useDeleteSession, useSessions } from '~/hooks/user/useSessions'

function SessionActions({ device, isCurrentDevice }: { device: string; isCurrentDevice: boolean }) {
  const deleteSession = useDeleteSession()
  const navigate = useNavigate()
  const { refetch } = useUserContext()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button
          variant='borderless'
          className='flex w-5 h-5 p-0 shadow-none hover:bg-transparent'
          aria-label='Session actions menu'
        >
          <MoreHorizontal className='w-4 h-4 text-[rgba(0,0,0,0.45)]' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem
          onClick={() => {
            deleteSession(device)
              .then(refetch)
              .then(() => navigate('/', { replace: true }))
          }}
          disabled={isCurrentDevice}
          className='text-tokens-status-error-text'
        >
          Logout Device
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function SessionsSection() {
  const { data: sessions, isLoading } = useSessions()

  if (isLoading) {
    return <div className='flex justify-center py-8 select-none'>Loading</div>
  }

  return (
    <div className='flex flex-col'>
      <SettingsSection>
        {/* Compact card layout for narrow viewports, table layout for wider */}

        {/* Table header - hidden on narrow, visible on sm+ */}
        <div className='hidden sm:flex items-center h-10 px-4 select-none'>
          <span className='flex-1 font-mono font-semibold text-[12px] leading-normal text-[rgba(0,0,0,0.65)]'>
            Device
          </span>
          <span className='w-[200px] font-mono font-semibold text-[12px] leading-normal text-[rgba(0,0,0,0.65)]'>
            Last Active
          </span>
          <span className='w-[60px] text-right font-mono font-semibold text-[12px] leading-normal text-[rgba(0,0,0,0.65)]'>
            Actions
          </span>
        </div>

        {sessions?.map((session, idx) => (
          <div key={session.device}>
            {/* Narrow viewport: stacked card layout */}
            <div className='sm:hidden'>
              {idx > 0 && <div className='h-px bg-tokens-neutral-border-2' />}
              <div className='flex items-start justify-between py-3 px-2 gap-2'>
                <div className='flex flex-col gap-1 min-w-0 flex-1'>
                  <div className='flex items-center gap-1.5 flex-wrap'>
                    <span className='font-sans font-bold text-[12px] leading-[18px] text-[rgba(0,0,0,0.88)]'>
                      {session.device}
                    </span>
                    {session.is_current_device && (
                      <span className='font-sans text-[10px] text-green-600 shrink-0 select-none'>
                        (current)
                      </span>
                    )}
                  </div>
                  <span className='font-sans font-medium text-[11px] leading-[16px] text-[rgba(0,0,0,0.45)]'>
                    {dayjs(session.last_active).format('YYYY-M-DD HH:mm')}
                  </span>
                </div>
                <div className='shrink-0 pt-0.5'>
                  <SessionActions
                    device={session.device}
                    isCurrentDevice={session.is_current_device}
                  />
                </div>
              </div>
            </div>

            {/* Wide viewport: table row layout */}
            <div className='hidden sm:flex items-center h-10 px-4'>
              <div className='flex-1 min-w-0'>
                <span className='font-sans font-bold text-[12px] leading-[18px] text-[rgba(0,0,0,0.88)]'>
                  {session.device}
                </span>
                {session.is_current_device && (
                  <span className='ml-2 font-sans text-[10px] text-green-600 select-none'>
                    (current)
                  </span>
                )}
              </div>
              <span className='w-[200px] font-sans font-medium text-[12px] leading-[16px] text-[rgba(0,0,0,0.88)] truncate'>
                {dayjs(session.last_active).format('YYYY-M-DD HH:mm')}
              </span>
              <div className='w-[60px] flex justify-end'>
                <SessionActions
                  device={session.device}
                  isCurrentDevice={session.is_current_device}
                />
              </div>
            </div>
          </div>
        ))}
      </SettingsSection>
    </div>
  )
}
